import BlockRegistry from './BlockRegistry';

// Helpers
const indent = (code, level = 1) =>
    code.split('\n')
        .map(line => (line.trim() ? `${'    '.repeat(level)}${line}` : ''))
        .join('\n');

const sanitiseVarName = name =>
    name.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^(\d)/, '_$1');

export function registerStandardBlocks() {

    // ─── Arduino Digital / Analog ────────────────────────────
    BlockRegistry.register('arduino_digital_write', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        const value = context.getInputValue(block, 'VALUE');
        context.addSetup(`pinMode(${pin}, OUTPUT);`);
        return `digitalWrite(${pin}, ${value})`;
    });
    BlockRegistry.registerPython('arduino_digital_write', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        const value = context.getInputValue(block, 'VALUE');
        context.addInclude('from machine import Pin');
        const pinVar = `pin_${pin}`;
        context.addSetup(`${pinVar} = Pin(${pin}, Pin.OUT)`);
        const pyVal = String(value).toUpperCase() === 'HIGH' ? '1' : (value === '1' ? '1' : '0');
        return `${pinVar}.value(${pyVal})`;
    });

    BlockRegistry.register('arduino_digital_read', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        context.addSetup(`pinMode(${pin}, INPUT);`);
        return `digitalRead(${pin})`;
    });
    BlockRegistry.registerPython('arduino_digital_read', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        context.addInclude('from machine import Pin');
        const pinVar = `pin_${pin}`;
        context.addSetup(`${pinVar} = Pin(${pin}, Pin.IN)`);
        return `${pinVar}.value()`;
    });

    BlockRegistry.register('arduino_analog_write', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        const value = context.getInputValue(block, 'VALUE');
        context.addSetup(`pinMode(${pin}, OUTPUT);`);
        return `analogWrite(${pin}, ${value})`;
    });
    BlockRegistry.registerPython('arduino_analog_write', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        const value = context.getInputValue(block, 'VALUE');
        context.addInclude('from machine import Pin, PWM');
        const pwmVar = `pwm_${pin}`;
        context.addSetup(`${pwmVar} = PWM(Pin(${pin}))`);
        context.addSetup(`${pwmVar}.freq(1000)`);
        return `${pwmVar}.duty_u16(int(float(${value}) * 257))`;
    });

    BlockRegistry.register('arduino_analog_read', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        return `analogRead(${pin})`;
    });
    BlockRegistry.registerPython('arduino_analog_read', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        context.addInclude('from machine import Pin, ADC');
        const adcVar = `adc_${pin}`;
        context.addSetup(`${adcVar} = ADC(Pin(${pin}))`);
        return `${adcVar}.read_u16()`;
    });

    BlockRegistry.register('arduino_set_pin_mode', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        const mode = context.getInputValue(block, 'MODE');
        return `pinMode(${pin}, ${mode})`;
    });
    BlockRegistry.registerPython('arduino_set_pin_mode', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        const mode = context.getInputValue(block, 'MODE');
        context.addInclude('from machine import Pin');
        const pyMode = String(mode).toUpperCase() === 'OUTPUT' ? 'Pin.OUT' : 'Pin.IN';
        return `Pin(${pin}, ${pyMode})`;
    });

    BlockRegistry.register('arduino_tone', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        const freq = context.getInputValue(block, 'FREQUENCY');
        const dur = context.getInputValue(block, 'DURATION');
        return `tone(${pin}, ${freq}, ${dur})`;
    });
    BlockRegistry.registerPython('arduino_tone', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        const freq = context.getInputValue(block, 'FREQUENCY');
        const dur = context.getInputValue(block, 'DURATION');
        context.addInclude('from machine import Pin, PWM\nimport time');
        const buzzerVar = `buzzer_${pin}`;
        context.addSetup(`${buzzerVar} = PWM(Pin(${pin}))`);
        return `${buzzerVar}.freq(int(${freq}))\n${buzzerVar}.duty_u16(32768)\ntime.sleep_ms(int(${dur}))\n${buzzerVar}.duty_u16(0)`;
    });

    BlockRegistry.register('arduino_no_tone', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        return `noTone(${pin})`;
    });
    BlockRegistry.registerPython('arduino_no_tone', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        const buzzerVar = `buzzer_${pin}`;
        return `${buzzerVar}.duty_u16(0)`;
    });

    BlockRegistry.register('arduino_servo_write', (block, context) => {
        context.addInclude('#include <Servo.h>');
        const pin = context.getInputValue(block, 'PIN');
        const angle = context.getInputValue(block, 'ANGLE');
        const servoName = `servo_pin_${pin}`;
        context.addGlobal(servoName, `Servo ${servoName};`);
        context.addSetup(`${servoName}.attach(${pin});`);
        return `${servoName}.write(${angle})`;
    });
    BlockRegistry.registerPython('arduino_servo_write', (block, context) => {
        const pin = context.getInputValue(block, 'PIN');
        const angle = context.getInputValue(block, 'ANGLE');
        context.addInclude('from machine import Pin, PWM');
        const servoVar = `servo_${pin}`;
        context.addSetup(`${servoVar} = PWM(Pin(${pin}))`);
        context.addSetup(`${servoVar}.freq(50)`);
        return `${servoVar}.duty_u16(int(float(${angle}) / 180.0 * 8000 + 1000))`;
    });

    BlockRegistry.register('arduino_serial_print', (block, context) => {
        const msg = context.getInputValue(block, 'MESSAGE');
        context.addSetup('Serial.begin(9600);');
        return `Serial.println(${msg})`;
    });
    BlockRegistry.registerPython('arduino_serial_print', (block, context) => {
        const msg = context.getInputValue(block, 'MESSAGE');
        return `print(${msg})`;
    });

    // ─── Control ────────────────────────────────────────────
    BlockRegistry.register('control_wait', (block, context) => {
        const secs = context.getInputValue(block, 'DURATION');
        return `delay(${secs} * 1000)`;
    });
    BlockRegistry.registerPython('control_wait', (block, context) => {
        const secs = context.getInputValue(block, 'DURATION');
        context.addInclude('import time');
        return `time.sleep(${secs})`;
    });

    BlockRegistry.register('control_forever', (block, context) => {
        const substack = context.generateSubstack(block, 'SUBSTACK');
        return `while (true) {\n${substack}\n}`;
    });
    BlockRegistry.registerPython('control_forever', (block, context) => {
        const substack = context.generateSubstack(block, 'SUBSTACK');
        return `while True:\n${substack || '    pass'}`;
    });

    BlockRegistry.register('control_repeat', (block, context) => {
        const times = context.getInputValue(block, 'TIMES');
        const substack = context.generateSubstack(block, 'SUBSTACK');
        return `for (int i = 0; i < ${times}; i++) {\n${substack}\n}`;
    });
    BlockRegistry.registerPython('control_repeat', (block, context) => {
        const times = context.getInputValue(block, 'TIMES');
        const substack = context.generateSubstack(block, 'SUBSTACK');
        return `for _ in range(${times}):\n${substack || '    pass'}`;
    });

    BlockRegistry.register('control_if', (block, context) => {
        const cond = context.getInputValue(block, 'CONDITION');
        const substack = context.generateSubstack(block, 'SUBSTACK');
        return `if (${cond}) {\n${substack}\n}`;
    });
    BlockRegistry.registerPython('control_if', (block, context) => {
        const cond = context.getInputValue(block, 'CONDITION') || 'False';
        const substack = context.generateSubstack(block, 'SUBSTACK');
        return `if ${cond}:\n${substack || '    pass'}`;
    });

    BlockRegistry.register('control_if_else', (block, context) => {
        const cond = context.getInputValue(block, 'CONDITION');
        const substack1 = context.generateSubstack(block, 'SUBSTACK');
        const substack2 = context.generateSubstack(block, 'SUBSTACK2');
        return `if (${cond}) {\n${substack1}\n} else {\n${substack2}\n}`;
    });
    BlockRegistry.registerPython('control_if_else', (block, context) => {
        const cond = context.getInputValue(block, 'CONDITION') || 'False';
        const substack1 = context.generateSubstack(block, 'SUBSTACK');
        const substack2 = context.generateSubstack(block, 'SUBSTACK2');
        return `if ${cond}:\n${substack1 || '    pass'}\nelse:\n${substack2 || '    pass'}`;
    });

    BlockRegistry.register('control_wait_until', (block, context) => {
        const cond = context.getInputValue(block, 'CONDITION');
        return `while (!(${cond})) { delay(1); }`;
    });
    BlockRegistry.registerPython('control_wait_until', (block, context) => {
        const cond = context.getInputValue(block, 'CONDITION') || 'False';
        context.addInclude('import time');
        return `while not (${cond}):\n    time.sleep_ms(10)`;
    });

    BlockRegistry.register('control_repeat_until', (block, context) => {
        const cond = context.getInputValue(block, 'CONDITION');
        const substack = context.generateSubstack(block, 'SUBSTACK');
        return `while (!(${cond})) {\n${substack}\n}`;
    });
    BlockRegistry.registerPython('control_repeat_until', (block, context) => {
        const cond = context.getInputValue(block, 'CONDITION') || 'False';
        const substack = context.generateSubstack(block, 'SUBSTACK');
        return `while not (${cond}):\n${substack || '    pass'}`;
    });

    // ─── Operators ──────────────────────────────────────────
    BlockRegistry.register('operator_add', (block, context) => `(${context.getInputValue(block, 'NUM1')} + ${context.getInputValue(block, 'NUM2')})`);
    BlockRegistry.registerPython('operator_add', (block, context) => `(${context.getInputValue(block, 'NUM1')} + ${context.getInputValue(block, 'NUM2')})`);

    BlockRegistry.register('operator_subtract', (block, context) => `(${context.getInputValue(block, 'NUM1')} - ${context.getInputValue(block, 'NUM2')})`);
    BlockRegistry.registerPython('operator_subtract', (block, context) => `(${context.getInputValue(block, 'NUM1')} - ${context.getInputValue(block, 'NUM2')})`);

    BlockRegistry.register('operator_multiply', (block, context) => `(${context.getInputValue(block, 'NUM1')} * ${context.getInputValue(block, 'NUM2')})`);
    BlockRegistry.registerPython('operator_multiply', (block, context) => `(${context.getInputValue(block, 'NUM1')} * ${context.getInputValue(block, 'NUM2')})`);

    BlockRegistry.register('operator_divide', (block, context) => `(${context.getInputValue(block, 'NUM1')} / ${context.getInputValue(block, 'NUM2')})`);
    BlockRegistry.registerPython('operator_divide', (block, context) => `(${context.getInputValue(block, 'NUM1')} / ${context.getInputValue(block, 'NUM2')})`);

    BlockRegistry.register('operator_gt', (block, context) => `(${context.getInputValue(block, 'OPERAND1')} > ${context.getInputValue(block, 'OPERAND2')})`);
    BlockRegistry.registerPython('operator_gt', (block, context) => `(${context.getInputValue(block, 'OPERAND1')} > ${context.getInputValue(block, 'OPERAND2')})`);

    BlockRegistry.register('operator_lt', (block, context) => `(${context.getInputValue(block, 'OPERAND1')} < ${context.getInputValue(block, 'OPERAND2')})`);
    BlockRegistry.registerPython('operator_lt', (block, context) => `(${context.getInputValue(block, 'OPERAND1')} < ${context.getInputValue(block, 'OPERAND2')})`);

    BlockRegistry.register('operator_equals', (block, context) => `(${context.getInputValue(block, 'OPERAND1')} == ${context.getInputValue(block, 'OPERAND2')})`);
    BlockRegistry.registerPython('operator_equals', (block, context) => `(${context.getInputValue(block, 'OPERAND1')} == ${context.getInputValue(block, 'OPERAND2')})`);

    BlockRegistry.register('operator_and', (block, context) => `(${context.getInputValue(block, 'OPERAND1')} && ${context.getInputValue(block, 'OPERAND2')})`);
    BlockRegistry.registerPython('operator_and', (block, context) => `(${context.getInputValue(block, 'OPERAND1')} and ${context.getInputValue(block, 'OPERAND2')})`);

    BlockRegistry.register('operator_or', (block, context) => `(${context.getInputValue(block, 'OPERAND1')} || ${context.getInputValue(block, 'OPERAND2')})`);
    BlockRegistry.registerPython('operator_or', (block, context) => `(${context.getInputValue(block, 'OPERAND1')} or ${context.getInputValue(block, 'OPERAND2')})`);

    BlockRegistry.register('operator_not', (block, context) => `(!${context.getInputValue(block, 'OPERAND')})`);
    BlockRegistry.registerPython('operator_not', (block, context) => `(not ${context.getInputValue(block, 'OPERAND')})`);

    BlockRegistry.register('operator_random', (block, context) => `random(${context.getInputValue(block, 'FROM')}, ${context.getInputValue(block, 'TO')})`);
    BlockRegistry.registerPython('operator_random', (block, context) => {
        context.addInclude('import random');
        return `random.randint(${context.getInputValue(block, 'FROM')}, ${context.getInputValue(block, 'TO')})`;
    });

    BlockRegistry.register('operator_mod', (block, context) => `((int)(${context.getInputValue(block, 'NUM1')}) % (int)(${context.getInputValue(block, 'NUM2')}))`);
    BlockRegistry.registerPython('operator_mod', (block, context) => `(int(${context.getInputValue(block, 'NUM1')}) % int(${context.getInputValue(block, 'NUM2')}))`);

    BlockRegistry.register('operator_round', (block, context) => `round(${context.getInputValue(block, 'NUM')})`);
    BlockRegistry.registerPython('operator_round', (block, context) => `round(${context.getInputValue(block, 'NUM')})`);
    
    BlockRegistry.register('operator_mathop', (block, context) => {
        const num = context.getInputValue(block, 'NUM');
        const op = context.getFieldValue(block, 'OPERATOR') || 'abs';
        switch (op) {
            case 'abs': return `abs(${num})`;
            case 'floor': return `floor(${num})`;
            case 'ceiling': return `ceil(${num})`;
            case 'sqrt': return `sqrt(${num})`;
            case 'sin': return `sin((${num}) * PI / 180.0)`;
            case 'cos': return `cos((${num}) * PI / 180.0)`;
            case 'tan': return `tan((${num}) * PI / 180.0)`;
            case 'asin': return `(asin(${num}) * 180.0 / PI)`;
            case 'acos': return `(acos(${num}) * 180.0 / PI)`;
            case 'atan': return `(atan(${num}) * 180.0 / PI)`;
            case 'ln': return `log(${num})`;
            case 'log': return `log10(${num})`;
            case 'e ^': return `exp(${num})`;
            case '10 ^': return `pow(10, ${num})`;
            default: return `0`;
        }
    });
    BlockRegistry.registerPython('operator_mathop', (block, context) => {
        context.addInclude('import math');
        const num = context.getInputValue(block, 'NUM');
        const op = context.getFieldValue(block, 'OPERATOR') || 'abs';
        switch (op) {
            case 'abs': return `abs(${num})`;
            case 'floor': return `math.floor(${num})`;
            case 'ceiling': return `math.ceil(${num})`;
            case 'sqrt': return `math.sqrt(${num})`;
            case 'sin': return `math.sin(math.radians(${num}))`;
            case 'cos': return `math.cos(math.radians(${num}))`;
            case 'tan': return `math.tan(math.radians(${num}))`;
            case 'asin': return `math.degrees(math.asin(${num}))`;
            case 'acos': return `math.degrees(math.acos(${num}))`;
            case 'atan': return `math.degrees(math.atan(${num}))`;
            case 'ln': return `math.log(${num})`;
            case 'log': return `math.log10(${num})`;
            case 'e ^': return `math.exp(${num})`;
            case '10 ^': return `math.pow(10, ${num})`;
            default: return `0`;
        }
    });

    BlockRegistry.register('operator_join', (block, context) => `(String(${context.getInputValue(block, 'STRING1')}) + String(${context.getInputValue(block, 'STRING2')}))`);
    BlockRegistry.registerPython('operator_join', (block, context) => `(str(${context.getInputValue(block, 'STRING1')}) + str(${context.getInputValue(block, 'STRING2')}))`);

    BlockRegistry.register('operator_letter_of', (block, context) => `String(${context.getInputValue(block, 'STRING')}).charAt(${context.getInputValue(block, 'LETTER')} - 1)`);
    BlockRegistry.registerPython('operator_letter_of', (block, context) => `str(${context.getInputValue(block, 'STRING')})[int(${context.getInputValue(block, 'LETTER')}) - 1]`);

    BlockRegistry.register('operator_length', (block, context) => `String(${context.getInputValue(block, 'STRING')}).length()`);
    BlockRegistry.registerPython('operator_length', (block, context) => `len(str(${context.getInputValue(block, 'STRING')}))`);

    BlockRegistry.register('operator_contains', (block, context) => `(String(${context.getInputValue(block, 'STRING1')}).indexOf(String(${context.getInputValue(block, 'STRING2')})) != -1)`);
    BlockRegistry.registerPython('operator_contains', (block, context) => `(str(${context.getInputValue(block, 'STRING2')}) in str(${context.getInputValue(block, 'STRING1')}))`);

    // ─── Data (Variables) ───────────────────────────────────
    BlockRegistry.register('data_setvariableto', (block, context) => {
        const varId = context.getFieldId(block, 'VARIABLE') || 'myVarId';
        const varRawName = context.getFieldValue(block, 'VARIABLE') || 'myVar';
        const varName = context.registerVariable(varId, varRawName);
        const val = context.getInputValue(block, 'VALUE');
        return `${varName} = ${val}`;
    });
    BlockRegistry.registerPython('data_setvariableto', (block, context) => {
        const varId = context.getFieldId(block, 'VARIABLE') || 'myVarId';
        const varRawName = context.getFieldValue(block, 'VARIABLE') || 'myVar';
        const varName = context.registerVariable(varId, varRawName);
        const val = context.getInputValue(block, 'VALUE');
        return `${varName} = ${val}`;
    });
    
    BlockRegistry.register('data_changevariableby', (block, context) => {
        const varId = context.getFieldId(block, 'VARIABLE') || 'myVarId';
        const varRawName = context.getFieldValue(block, 'VARIABLE') || 'myVar';
        const varName = context.registerVariable(varId, varRawName);
        const val = context.getInputValue(block, 'VALUE');
        return `${varName} += ${val}`;
    });
    BlockRegistry.registerPython('data_changevariableby', (block, context) => {
        const varId = context.getFieldId(block, 'VARIABLE') || 'myVarId';
        const varRawName = context.getFieldValue(block, 'VARIABLE') || 'myVar';
        const varName = context.registerVariable(varId, varRawName);
        const val = context.getInputValue(block, 'VALUE');
        return `${varName} += ${val}`;
    });

    BlockRegistry.register('data_variable', (block, context) => {
        const varId = context.getFieldId(block, 'VARIABLE') || 'myVarId';
        const varRawName = context.getFieldValue(block, 'VARIABLE') || 'myVar';
        return context.registerVariable(varId, varRawName);
    });
    BlockRegistry.registerPython('data_variable', (block, context) => {
        const varId = context.getFieldId(block, 'VARIABLE') || 'myVarId';
        const varRawName = context.getFieldValue(block, 'VARIABLE') || 'myVar';
        return context.registerVariable(varId, varRawName);
    });

    // ─── My Blocks (Procedures) ─────────────────────────────
    BlockRegistry.register('procedures_definition', (block, context) => {
        const customBlockId = block.inputs && block.inputs.custom_block && (block.inputs.custom_block.block || block.inputs.custom_block[1]);
        const customBlock = context.blocks[customBlockId];
        if (!customBlock) return '';
        
        const mutation = customBlock.mutation;
        const proccode = mutation.proccode || 'myFunction';
        let argNames = [];
        try {
            if (mutation.argumentnames) argNames = JSON.parse(mutation.argumentnames);
        } catch (e) {}
        
        const funcName = sanitiseVarName(proccode.split(' ')[0] || 'myFunction');
        const argsList = argNames.map(name => `float ${sanitiseVarName(name)}`).join(', ');
        
        // Build substack
        let currentId = block.next;
        const lines = [];
        while (currentId) {
            const b = context.blocks[currentId];
            if (!b) break;
            const code = BlockRegistry.generate(b, context);
            if (code) lines.push(code + (code.endsWith('}') || code.endsWith(';') ? '' : ';'));
            currentId = b.next;
        }
        
        const substack = lines.map(line => `    ${line}`).join('\n');
        context.addHelper(funcName, `void ${funcName}(${argsList}) {\n${substack}\n}`);
        return ''; 
    });

    BlockRegistry.register('procedures_call', (block, context) => {
        const mutation = block.mutation;
        const proccode = mutation.proccode || 'myFunction';
        const funcName = sanitiseVarName(proccode.split(' ')[0] || 'myFunction');
        
        let argIds = [];
        try {
            if (mutation.argumentids) argIds = JSON.parse(mutation.argumentids);
        } catch (e) {}
        
        const args = argIds.map(id => context.getInputValue(block, id));
        return `${funcName}(${args.join(', ')})`;
    });

    BlockRegistry.register('argument_reporter_string_number', (block, context) => sanitiseVarName(context.getFieldValue(block, 'VALUE') || 'arg'));
    BlockRegistry.register('argument_reporter_boolean', (block, context) => sanitiseVarName(context.getFieldValue(block, 'VALUE') || 'arg'));

    // ─── Primitives ─────────────────────────────────────────
    BlockRegistry.register('text', (block, context) => `"${context.getFieldValue(block, 'TEXT')}"`);
    BlockRegistry.register('math_number', (block, context) => context.getFieldValue(block, 'NUM'));
    BlockRegistry.register('math_positive_number', (block, context) => context.getFieldValue(block, 'NUM'));
    BlockRegistry.register('math_whole_number', (block, context) => context.getFieldValue(block, 'NUM'));
    BlockRegistry.register('math_integer', (block, context) => context.getFieldValue(block, 'NUM'));
    BlockRegistry.register('math_angle', (block, context) => context.getFieldValue(block, 'NUM'));
    BlockRegistry.register('colour_picker', (block, context) => context.getFieldValue(block, 'COLOUR'));
}
